import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import {
    CreateThemeBL,
    UpdateThemeBL,
    UploadAssetBL
} from '../../../../utilities/BL/CarreraContinua';
import { GetActiveStadiumZones } from '../../../../utilities/BL/Zones';

// Helper para resolver URL de imágenes completas
const getFullAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
    }
    const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const apiRoot = apiUrl.replace(/\/api\/?$/, '');
    return `${apiRoot}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Componente Drag & Drop Reutilizable con Carga de Archivos
 */
const FileDropzone = ({
    label,
    value,
    onUpload,
    onDelete,
    fieldName,
    subIndex = -1,
    subField = null,
    uploadingKey = null,
    accept = 'image/png, image/jpeg, image/webp, image/svg+xml',
    aspectHint = 'PNG / WebP / SVG',
    previewHeight = '75px'
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const inputId = `file-dropzone-${fieldName}-${subIndex >= 0 ? subIndex : 'single'}-${subField || ''}`;
    const myKey = `${fieldName}${subIndex >= 0 ? `-${subIndex}` : ''}${subField ? `-${subField}` : ''}`;
    const isUploading = uploadingKey === myKey;

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            onUpload(files[0], fieldName, subIndex, subField);
        }
    };

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onUpload(files[0], fieldName, subIndex, subField);
        }
    };

    return (
        <div className="mb-3">
            {label && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-bold small text-secondary mb-0">{label}</label>
                    {aspectHint && <span className="badge bg-light text-muted border small">{aspectHint}</span>}
                </div>
            )}
            <div
                className={`border rounded-3 p-2 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${
                    isDragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed bg-light'
                }`}
                style={{
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    minHeight: '100px',
                    transition: 'all 0.2s ease-in-out',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const el = document.getElementById(inputId);
                        if (el) el.click();
                    }
                }}
            >
                <input
                    type="file"
                    id={inputId}
                    className="d-none"
                    accept={accept}
                    onChange={handleFileChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="d-flex flex-column align-items-center py-2">
                        <div className="spinner-border spinner-border-sm text-primary mb-1" role="status"></div>
                        <span className="small text-muted fw-semibold">Procesando y optimizando...</span>
                    </div>
                ) : value ? (
                    <div className="position-relative d-flex flex-column align-items-center w-100" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getFullAssetUrl(value)}
                            alt="Vista previa"
                            className="img-thumbnail mb-1 shadow-sm bg-white"
                            style={{ maxHeight: previewHeight, maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="%23999" viewBox="0 0 16 16"><path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/><path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/></svg>';
                            }}
                        />
                        <div className="d-flex gap-2 align-items-center mt-1">
                            <span className="small text-truncate text-muted d-block" style={{ maxWidth: '180px' }} title={value}>
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger border-0 p-0 px-1"
                                title="Eliminar imagen"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(fieldName, subIndex, subField);
                                }}
                            >
                                <i className="bi bi-trash3-fill"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary border-0 p-0 px-1"
                                title="Reemplazar imagen"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const el = document.getElementById(inputId);
                                    if (el) el.click();
                                }}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex flex-column align-items-center text-secondary small py-2">
                        <i className="bi bi-cloud-arrow-up-fill fs-3 text-primary mb-1"></i>
                        <span className="fw-semibold">Arrastra o haz clic para subir</span>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Modal de Administración de Temas de Carrera Continua
 */
const CarreraContinuaThemeModal = ({
    show,
    onClose,
    onSave,
    themeToEdit = null,
    roomId = null,
    operatorId = null
}) => {
    const { showMessage } = useFloatMessage();

    // Pestaña Activa: 'general', 'cover', 'scenery', 'checkpoints', 'runners'
    const [activeTab, setActiveTab] = useState('general');
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingKey, setUploadingKey] = useState(null);

    // =========================================================================
    // ESTADOS DEL FORMULARIO DE TEMA
    // =========================================================================
    
    // Pestaña 1: General
    const [themeName, setThemeName] = useState('');
    const [fontFamily, setFontFamily] = useState('Montserrat, sans-serif');
    const [primaryColor, setPrimaryColor] = useState('#0d6efd');
    const [secondaryColor, setSecondaryColor] = useState('#ffc107');
    const [brandLogoUrl, setBrandLogoUrl] = useState('');
    const [sponsorLogoUrl, setSponsorLogoUrl] = useState('');
    const [enableFoamArches, setEnableFoamArches] = useState(true);
    const [totalKilometers, setTotalKilometers] = useState(5);

    // Pestaña 2: Portada
    const [coverTitle, setCoverTitle] = useState('');
    const [coverBackgroundUrl, setCoverBackgroundUrl] = useState('');

    // Pestaña 3: Escenario & Parallax
    const [skyBackgroundUrl, setSkyBackgroundUrl] = useState('');
    const [cityBackgroundUrl, setCityBackgroundUrl] = useState('');
    const [trackBackgroundUrl, setTrackBackgroundUrl] = useState('');
    const [fanBackgroundUrl, setFanBackgroundUrl] = useState('');

    // Pestaña 4: Arcos & Espuma
    const [defaultArchImageUrl, setDefaultArchImageUrl] = useState('');
    const [checkpoints, setCheckpoints] = useState([]);

    // Pestaña 5: Corredores / Zonas
    const [runners, setRunners] = useState([]);

    // =========================================================================
    // GENERADORES POR DEFECTO
    // =========================================================================

    const generateDefaultCheckpoints = useCallback((kmCount) => {
        const count = Math.max(1, Number(kmCount) || 5);
        const generated = [];
        const foamColors = ['#00e5ff', '#ff007f', '#00ff66', '#ffbb00', '#ffd700', '#9900ff', '#ff3300'];

        for (let i = 1; i <= count; i++) {
            const pct = Math.round((i / count) * 100);
            const isFinish = i === count;
            generated.push({
                kilometer: i,
                kilometerIndex: i,
                triggerPercentage: pct,
                label: isFinish ? '¡Meta Final!' : `Km ${i}`,
                labelText: isFinish ? '¡Meta Final!' : `Km ${i}`,
                foamColorHex: foamColors[(i - 1) % foamColors.length],
                hasFoam: true,
                archImageUrl: '',
                bannerImageUrl: '',
                sponsorLogoUrl: ''
            });
        }
        return generated;
    }, []);

    const generateDefaultRunners = useCallback(() => {
        return [
            {
                zoneId: '1',
                zoneIdentifier: '1',
                zoneName: 'Zona 1 (Norte / Azul)',
                displayName: 'Zona 1 (Norte / Azul)',
                color: '#0d6efd',
                themeColorHex: '#0d6efd',
                avatarUrl: '',
                avatarRunUrl: '',
                spriteUrl: '',
                avatarWinUrl: ''
            },
            {
                zoneId: '2',
                zoneIdentifier: '2',
                zoneName: 'Zona 2 (Sur / Roja)',
                displayName: 'Zona 2 (Sur / Roja)',
                color: '#dc3545',
                themeColorHex: '#dc3545',
                avatarUrl: '',
                avatarRunUrl: '',
                spriteUrl: '',
                avatarWinUrl: ''
            },
            {
                zoneId: '3',
                zoneIdentifier: '3',
                zoneName: 'Zona 3 (Oriente / Verde)',
                displayName: 'Zona 3 (Oriente / Verde)',
                color: '#198754',
                themeColorHex: '#198754',
                avatarUrl: '',
                avatarRunUrl: '',
                spriteUrl: '',
                avatarWinUrl: ''
            },
            {
                zoneId: '4',
                zoneIdentifier: '4',
                zoneName: 'Zona 4 (Poniente / Amarillo)',
                displayName: 'Zona 4 (Poniente / Amarillo)',
                color: '#ffc107',
                themeColorHex: '#ffc107',
                avatarUrl: '',
                avatarRunUrl: '',
                spriteUrl: '',
                avatarWinUrl: ''
            }
        ];
    }, []);

    // =========================================================================
    // EFECTO DE CARGA Y POBLACIÓN DE ESTADOS
    // =========================================================================
    useEffect(() => {
        if (!show) return;

        setActiveTab('general');

        if (themeToEdit) {
            // Cargar datos existentes del tema
            setThemeName(themeToEdit.name || '');
            setFontFamily(themeToEdit.fontFamily || 'Montserrat, sans-serif');
            setPrimaryColor(themeToEdit.primaryColor || '#0d6efd');
            setSecondaryColor(themeToEdit.secondaryColor || '#ffc107');
            setBrandLogoUrl(themeToEdit.brandLogoUrl || '');
            setSponsorLogoUrl(themeToEdit.sponsorLogoUrl || '');
            setEnableFoamArches(themeToEdit.enableFoamArches !== undefined ? themeToEdit.enableFoamArches : true);
            const km = themeToEdit.totalKilometers || 5;
            setTotalKilometers(km);

            setCoverTitle(themeToEdit.coverTitle || '');
            setCoverBackgroundUrl(themeToEdit.coverBackgroundUrl || '');

            setSkyBackgroundUrl(themeToEdit.skyBackgroundUrl || '');
            setCityBackgroundUrl(themeToEdit.cityBackgroundUrl || '');
            setTrackBackgroundUrl(themeToEdit.trackBackgroundUrl || '');
            setFanBackgroundUrl(themeToEdit.fanBackgroundUrl || '');
            setDefaultArchImageUrl(themeToEdit.defaultArchImageUrl || themeToEdit.archImageUrl || '');

            // Checkpoints
            let parsedCheckpoints = [];
            if (themeToEdit.checkpointsConfigJson) {
                try {
                    parsedCheckpoints = typeof themeToEdit.checkpointsConfigJson === 'string'
                        ? JSON.parse(themeToEdit.checkpointsConfigJson)
                        : themeToEdit.checkpointsConfigJson;
                } catch (e) {
                    parsedCheckpoints = [];
                }
            }
            if (Array.isArray(parsedCheckpoints) && parsedCheckpoints.length > 0) {
                setCheckpoints(parsedCheckpoints.map((cp, idx) => ({
                    kilometer: cp.kilometer || cp.kilometerIndex || (idx + 1),
                    kilometerIndex: cp.kilometerIndex || cp.kilometer || (idx + 1),
                    triggerPercentage: cp.triggerPercentage !== undefined ? cp.triggerPercentage : Math.round(((idx + 1) / (km || 5)) * 100),
                    label: cp.label || cp.labelText || `Km ${idx + 1}`,
                    labelText: cp.labelText || cp.label || `Km ${idx + 1}`,
                    foamColorHex: cp.foamColorHex || '#00e5ff',
                    hasFoam: cp.hasFoam !== undefined ? cp.hasFoam : true,
                    archImageUrl: cp.archImageUrl || cp.bannerImageUrl || '',
                    bannerImageUrl: cp.bannerImageUrl || cp.archImageUrl || '',
                    sponsorLogoUrl: cp.sponsorLogoUrl || ''
                })));
            } else {
                setCheckpoints(generateDefaultCheckpoints(km));
            }

            // Runners
            let parsedRunners = [];
            if (themeToEdit.runnersConfigJson) {
                try {
                    parsedRunners = typeof themeToEdit.runnersConfigJson === 'string'
                        ? JSON.parse(themeToEdit.runnersConfigJson)
                        : themeToEdit.runnersConfigJson;
                } catch (e) {
                    parsedRunners = [];
                }
            }
            if (Array.isArray(parsedRunners) && parsedRunners.length > 0) {
                setRunners(parsedRunners.map((r, idx) => ({
                    zoneId: r.zoneId || r.zoneIdentifier || String(idx + 1),
                    zoneIdentifier: r.zoneIdentifier || r.zoneId || String(idx + 1),
                    zoneName: r.zoneName || r.displayName || `Zona ${idx + 1}`,
                    displayName: r.displayName || r.zoneName || `Zona ${idx + 1}`,
                    color: r.color || r.themeColorHex || '#0d6efd',
                    themeColorHex: r.themeColorHex || r.color || '#0d6efd',
                    avatarUrl: r.avatarUrl || r.avatarRunUrl || '',
                    avatarRunUrl: r.avatarRunUrl || r.avatarUrl || '',
                    spriteUrl: r.spriteUrl || r.avatarWinUrl || '',
                    avatarWinUrl: r.avatarWinUrl || r.spriteUrl || ''
                })));
            } else {
                setRunners(generateDefaultRunners());
            }

        } else {
            // Tema Nuevo: Valores por defecto
            setThemeName('');
            setFontFamily('Montserrat, sans-serif');
            setPrimaryColor('#0d6efd');
            setSecondaryColor('#ffc107');
            setBrandLogoUrl('');
            setSponsorLogoUrl('');
            setEnableFoamArches(true);
            setTotalKilometers(5);

            setCoverTitle('¡Gran Carrera Continua!');
            setCoverBackgroundUrl('');

            setSkyBackgroundUrl('');
            setCityBackgroundUrl('');
            setTrackBackgroundUrl('');
            setFanBackgroundUrl('');
            setDefaultArchImageUrl('');

            setCheckpoints(generateDefaultCheckpoints(5));
            setRunners(generateDefaultRunners());

            // Intentar auto-sincronizar zonas de la sala activa si están disponibles
            if (roomId) {
                GetActiveStadiumZones(roomId).then((res) => {
                    if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
                        const syncedRunners = res.data.map((z, idx) => {
                            const colors = ['#0d6efd', '#dc3545', '#198754', '#ffc107', '#6f42c1', '#0dcaf0'];
                            const color = colors[idx % colors.length];
                            return {
                                zoneId: String(z.id || z.internalName || (idx + 1)),
                                zoneIdentifier: String(z.internalName || z.id || (idx + 1)),
                                zoneName: z.displayName || `Zona ${idx + 1}`,
                                displayName: z.displayName || `Zona ${idx + 1}`,
                                color: color,
                                themeColorHex: color,
                                avatarUrl: '',
                                avatarRunUrl: '',
                                spriteUrl: '',
                                avatarWinUrl: ''
                            };
                        });
                        setRunners(syncedRunners);
                    }
                }).catch(() => {});
            }
        }
    }, [show, themeToEdit, roomId, generateDefaultCheckpoints, generateDefaultRunners]);

    // =========================================================================
    // SUBIDA Y GESTIÓN DE RECURSOS CON DRAG & DROP
    // =========================================================================
    const handleUploadAsset = async (file, fieldName, subIndex = -1, subField = null) => {
        if (!file) return;

        const myKey = `${fieldName}${subIndex >= 0 ? `-${subIndex}` : ''}${subField ? `-${subField}` : ''}`;
        setUploadingKey(myKey);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await UploadAssetBL(formData);
            if (res.success && res.data) {
                const uploadedUrl = res.data.url || res.data.data?.url || res.url;
                if (!uploadedUrl) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se obtuvo la URL del recurso subido.' });
                    return;
                }

                // Asignar según campo
                if (subIndex >= 0 && subField) {
                    if (fieldName === 'checkpoints') {
                        setCheckpoints((prev) => {
                            const updated = [...prev];
                            updated[subIndex] = {
                                ...updated[subIndex],
                                [subField]: uploadedUrl,
                                ...(subField === 'archImageUrl' ? { bannerImageUrl: uploadedUrl } : {})
                            };
                            return updated;
                        });
                    } else if (fieldName === 'runners') {
                        setRunners((prev) => {
                            const updated = [...prev];
                            updated[subIndex] = {
                                ...updated[subIndex],
                                [subField]: uploadedUrl,
                                ...(subField === 'avatarRunUrl' ? { avatarUrl: uploadedUrl } : {}),
                                ...(subField === 'avatarWinUrl' ? { spriteUrl: uploadedUrl } : {})
                            };
                            return updated;
                        });
                    }
                } else {
                    switch (fieldName) {
                        case 'brandLogoUrl': setBrandLogoUrl(uploadedUrl); break;
                        case 'sponsorLogoUrl': setSponsorLogoUrl(uploadedUrl); break;
                        case 'coverBackgroundUrl': setCoverBackgroundUrl(uploadedUrl); break;
                        case 'skyBackgroundUrl':
                        case 'cityBackgroundUrl':
                            setCityBackgroundUrl(uploadedUrl);
                            setSkyBackgroundUrl(uploadedUrl);
                            break;
                        case 'trackBackgroundUrl': setTrackBackgroundUrl(uploadedUrl); break;
                        case 'fanBackgroundUrl': setFanBackgroundUrl(uploadedUrl); break;
                        case 'defaultArchImageUrl':
                        case 'archImageUrl':
                            setDefaultArchImageUrl(uploadedUrl);
                            setCheckpoints((prev) => prev.map((cp) => ({
                                ...cp,
                                archImageUrl: cp.archImageUrl || uploadedUrl,
                                bannerImageUrl: cp.bannerImageUrl || uploadedUrl
                            })));
                            break;
                        default: break;
                    }
                }

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo procesado y subido correctamente.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al procesar la imagen.' });
            }
        } catch (err) {
            console.error('[CarreraContinuaThemeModal] Upload error:', err);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al subir el archivo.' });
        } finally {
            setUploadingKey(null);
        }
    };

    const handleDeleteAsset = (fieldName, subIndex = -1, subField = null) => {
        if (subIndex >= 0 && subField) {
            if (fieldName === 'checkpoints') {
                setCheckpoints((prev) => {
                    const updated = [...prev];
                    updated[subIndex] = {
                        ...updated[subIndex],
                        [subField]: '',
                        ...(subField === 'archImageUrl' ? { bannerImageUrl: '' } : {})
                    };
                    return updated;
                });
            } else if (fieldName === 'runners') {
                setRunners((prev) => {
                    const updated = [...prev];
                    updated[subIndex] = {
                        ...updated[subIndex],
                        [subField]: '',
                        ...(subField === 'avatarRunUrl' ? { avatarUrl: '' } : {}),
                        ...(subField === 'avatarWinUrl' ? { spriteUrl: '' } : {})
                    };
                    return updated;
                });
            }
        } else {
            switch (fieldName) {
                case 'brandLogoUrl': setBrandLogoUrl(''); break;
                case 'sponsorLogoUrl': setSponsorLogoUrl(''); break;
                case 'coverBackgroundUrl': setCoverBackgroundUrl(''); break;
                case 'skyBackgroundUrl':
                case 'cityBackgroundUrl':
                    setCityBackgroundUrl('');
                    setSkyBackgroundUrl('');
                    break;
                case 'trackBackgroundUrl': setTrackBackgroundUrl(''); break;
                case 'fanBackgroundUrl': setFanBackgroundUrl(''); break;
                case 'defaultArchImageUrl':
                case 'archImageUrl':
                    setDefaultArchImageUrl('');
                    break;
                default: break;
            }
        }
    };

    // =========================================================================
    // ACCIONES DE ARCOS / HITOS
    // =========================================================================
    const handleAddCheckpoint = () => {
        setCheckpoints((prev) => {
            const nextKm = prev.length + 1;
            const foamColors = ['#00e5ff', '#ff007f', '#00ff66', '#ffbb00', '#ffd700', '#9900ff', '#ff3300'];
            const newCp = {
                kilometer: nextKm,
                kilometerIndex: nextKm,
                triggerPercentage: Math.min(100, Math.round((nextKm / Math.max(nextKm, totalKilometers)) * 100)),
                label: `Km ${nextKm}`,
                labelText: `Km ${nextKm}`,
                foamColorHex: foamColors[(nextKm - 1) % foamColors.length],
                hasFoam: true,
                archImageUrl: '',
                bannerImageUrl: '',
                sponsorLogoUrl: ''
            };
            return [...prev, newCp];
        });
    };

    const handleRemoveCheckpoint = (index) => {
        setCheckpoints((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpdateCheckpoint = (index, key, val) => {
        setCheckpoints((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [key]: val,
                ...(key === 'labelText' ? { label: val } : {}),
                ...(key === 'kilometerIndex' ? { kilometer: Number(val) } : {})
            };
            return updated;
        });
    };

    const handleRegenerateCheckpoints = () => {
        if (window.confirm(`¿Regenerar automáticamente los hitos para ${totalKilometers} Km? Se reemplazarán los hitos actuales.`)) {
            setCheckpoints(generateDefaultCheckpoints(totalKilometers));
        }
    };

    // =========================================================================
    // ACCIONES DE CORREDORES / ZONAS
    // =========================================================================
    const handleAddRunner = () => {
        setRunners((prev) => {
            const nextIdx = prev.length + 1;
            const colors = ['#0d6efd', '#dc3545', '#198754', '#ffc107', '#6f42c1', '#0dcaf0'];
            const color = colors[(nextIdx - 1) % colors.length];
            const newRunner = {
                zoneId: String(nextIdx),
                zoneIdentifier: String(nextIdx),
                zoneName: `Zona ${nextIdx}`,
                displayName: `Zona ${nextIdx}`,
                color: color,
                themeColorHex: color,
                avatarUrl: '',
                avatarRunUrl: '',
                spriteUrl: '',
                avatarWinUrl: ''
            };
            return [...prev, newRunner];
        });
    };

    const handleRemoveRunner = (index) => {
        if (runners.length <= 1) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debe existir al menos un corredor o zona.' });
            return;
        }
        setRunners((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpdateRunner = (index, key, val) => {
        setRunners((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [key]: val,
                ...(key === 'displayName' ? { zoneName: val } : {}),
                ...(key === 'zoneIdentifier' ? { zoneId: val } : {}),
                ...(key === 'themeColorHex' ? { color: val } : {})
            };
            return updated;
        });
    };

    const handleSyncStadiumZones = async () => {
        try {
            const res = await GetActiveStadiumZones(roomId);
            if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
                const colors = ['#0d6efd', '#dc3545', '#198754', '#ffc107', '#6f42c1', '#0dcaf0'];
                const synced = res.data.map((z, idx) => ({
                    zoneId: String(z.id || z.internalName || (idx + 1)),
                    zoneIdentifier: String(z.internalName || z.id || (idx + 1)),
                    zoneName: z.displayName || `Zona ${idx + 1}`,
                    displayName: z.displayName || `Zona ${idx + 1}`,
                    color: colors[idx % colors.length],
                    themeColorHex: colors[idx % colors.length],
                    avatarUrl: '',
                    avatarRunUrl: '',
                    spriteUrl: '',
                    avatarWinUrl: ''
                }));
                setRunners(synced);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Sincronizadas ${synced.length} zonas de la sala.` });
            } else {
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'No se encontraron zonas configuradas en esta sala. Se mantienen las actuales.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al consultar zonas de la sala.' });
        }
    };

    // =========================================================================
    // GUARDADO Y PERSISTENCIA
    // =========================================================================
    const handleSaveTheme = async (e) => {
        if (e) e.preventDefault();

        if (!themeName || !themeName.trim()) {
            setActiveTab('general');
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre del tema es obligatorio.' });
            return;
        }

        setIsSaving(true);
        try {
            // Homogeneizar JSONs
            const checkpointsClean = checkpoints.map((cp, idx) => ({
                kilometer: Number(cp.kilometerIndex || cp.kilometer || (idx + 1)),
                kilometerIndex: Number(cp.kilometerIndex || cp.kilometer || (idx + 1)),
                triggerPercentage: Number(cp.triggerPercentage) || 0,
                label: cp.labelText || cp.label || `Km ${idx + 1}`,
                labelText: cp.labelText || cp.label || `Km ${idx + 1}`,
                foamColorHex: cp.foamColorHex || '#00e5ff',
                hasFoam: cp.hasFoam !== undefined ? Boolean(cp.hasFoam) : true,
                archImageUrl: cp.archImageUrl || cp.bannerImageUrl || defaultArchImageUrl || '',
                bannerImageUrl: cp.archImageUrl || cp.bannerImageUrl || defaultArchImageUrl || '',
                sponsorLogoUrl: cp.sponsorLogoUrl || ''
            }));

            const runnersClean = runners.map((r, idx) => ({
                zoneId: String(r.zoneIdentifier || r.zoneId || (idx + 1)),
                zoneIdentifier: String(r.zoneIdentifier || r.zoneId || (idx + 1)),
                zoneName: r.displayName || r.zoneName || `Zona ${idx + 1}`,
                displayName: r.displayName || r.zoneName || `Zona ${idx + 1}`,
                color: r.themeColorHex || r.color || '#0d6efd',
                themeColorHex: r.themeColorHex || r.color || '#0d6efd',
                avatarUrl: r.avatarRunUrl || r.avatarUrl || '',
                avatarRunUrl: r.avatarRunUrl || r.avatarUrl || '',
                spriteUrl: r.avatarWinUrl || r.spriteUrl || '',
                avatarWinUrl: r.avatarWinUrl || r.spriteUrl || ''
            }));

            const payload = {
                name: themeName.trim(),
                roomId: roomId || null,
                operatorId: operatorId || null,
                isSystemPreset: false,
                brandLogoUrl: brandLogoUrl || null,
                sponsorLogoUrl: sponsorLogoUrl || null,
                coverTitle: coverTitle || null,
                coverBackgroundUrl: coverBackgroundUrl || null,
                skyBackgroundUrl: skyBackgroundUrl || null,
                cityBackgroundUrl: cityBackgroundUrl || null,
                trackBackgroundUrl: trackBackgroundUrl || null,
                fanBackgroundUrl: fanBackgroundUrl || null,
                defaultArchImageUrl: defaultArchImageUrl || null,
                archImageUrl: defaultArchImageUrl || null,
                enableFoamArches: Boolean(enableFoamArches),
                totalKilometers: Number(totalKilometers) || 5,
                checkpointsConfigJson: JSON.stringify(checkpointsClean),
                runnersConfigJson: JSON.stringify(runnersClean),
                fontFamily: fontFamily || 'Montserrat, sans-serif',
                primaryColor: primaryColor || '#0d6efd',
                secondaryColor: secondaryColor || '#ffc107'
            };

            let res;
            if (themeToEdit && themeToEdit.id) {
                res = await UpdateThemeBL(themeToEdit.id, payload);
            } else {
                res = await CreateThemeBL(payload);
            }

            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: themeToEdit?.id ? 'Tema actualizado exitosamente.' : 'Tema creado exitosamente.'
                });
                if (onSave) onSave(res.data);
                if (onClose) onClose();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar el tema.' });
            }
        } catch (err) {
            console.error('[CarreraContinuaThemeModal] Save error:', err);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el tema.' });
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) return null;

    return (
        <>
            {/* BACKDROP CON Z-INDEX: 99998 */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    zIndex: 99998,
                    backdropFilter: 'blur(5px)'
                }}
            />

            {/* CONTENEDOR MODAL CON Z-INDEX: 99999 */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '920px',
                    maxHeight: '92vh',
                    zIndex: 99999,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="card border-0 shadow-lg rounded-4 overflow-hidden d-flex flex-column h-100 bg-white">
                    
                    {/* ENCABEZADO DEL MODAL */}
                    <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3 px-4 flex-shrink-0">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill fs-4 text-warning"></i>
                            <div>
                                <h5 className="mb-0 fw-bold">
                                    {themeToEdit ? `🎨 Editar Tema: ${themeToEdit.name}` : '🎨 Crear Nuevo Tema de Carrera'}
                                </h5>
                                <small className="text-muted">Personalizador Visual con SkiaSharp y Drag & Drop</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            onClick={onClose}
                            disabled={isSaving}
                        />
                    </div>

                    {/* BARRA DE NAVEGACIÓN EN 5 PESTAÑAS */}
                    <div className="bg-light border-bottom px-3 pt-2 flex-shrink-0">
                        <ul className="nav nav-pills nav-fill gap-1">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link py-2 fw-bold small d-flex align-items-center justify-content-center gap-1 ${activeTab === 'general' ? 'active shadow-sm' : 'text-dark'}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    <i className="bi bi-gear-fill"></i>
                                    1. General
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link py-2 fw-bold small d-flex align-items-center justify-content-center gap-1 ${activeTab === 'cover' ? 'active shadow-sm' : 'text-dark'}`}
                                    onClick={() => setActiveTab('cover')}
                                >
                                    <i className="bi bi-card-image"></i>
                                    2. Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link py-2 fw-bold small d-flex align-items-center justify-content-center gap-1 ${activeTab === 'scenery' ? 'active shadow-sm' : 'text-dark'}`}
                                    onClick={() => setActiveTab('scenery')}
                                >
                                    <i className="bi bi-layers-fill"></i>
                                    3. Escenario
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link py-2 fw-bold small d-flex align-items-center justify-content-center gap-1 ${activeTab === 'checkpoints' ? 'active shadow-sm' : 'text-dark'}`}
                                    onClick={() => setActiveTab('checkpoints')}
                                >
                                    <i className="bi bi-flag-fill"></i>
                                    4. Arcos & Espuma
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link py-2 fw-bold small d-flex align-items-center justify-content-center gap-1 ${activeTab === 'runners' ? 'active shadow-sm' : 'text-dark'}`}
                                    onClick={() => setActiveTab('runners')}
                                >
                                    <i className="bi bi-people-fill"></i>
                                    5. Corredores / Zonas
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* CUERPO DEL FORMULARIO CON SCROLL */}
                    <div className="card-body p-4 overflow-y-auto" style={{ maxHeight: 'calc(92vh - 170px)' }}>
                        <form onSubmit={handleSaveTheme}>
                            
                            {/* ========================================================================= */}
                            {/* PESTAÑA 1: GENERAL */}
                            {/* ========================================================================= */}
                            {activeTab === 'general' && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                        <i className="bi bi-info-circle-fill me-2 text-primary"></i>
                                        Identidad y Reglas de Carrera
                                    </h6>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-8">
                                            <label className="form-label fw-bold small text-secondary">
                                                Nombre del Tema <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ej. Maratón Clásico 5K, Nocturna Neón, etc."
                                                value={themeName}
                                                onChange={(e) => setThemeName(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold small text-secondary">
                                                Distancia en Kilómetros
                                            </label>
                                            <select
                                                className="form-select"
                                                value={totalKilometers}
                                                onChange={(e) => {
                                                    const km = Number(e.target.value);
                                                    setTotalKilometers(km);
                                                }}
                                            >
                                                <option value={3}>3K (3 Kilómetros)</option>
                                                <option value={5}>5K (5 Kilómetros)</option>
                                                <option value={10}>10K (10 Kilómetros)</option>
                                                <option value={15}>15K (15 Kilómetros)</option>
                                                <option value={21}>21K (Medio Maratón)</option>
                                                <option value={42}>42K (Maratón Completo)</option>
                                            </select>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small text-secondary">
                                                Tipografía Principal
                                            </label>
                                            <select
                                                className="form-select"
                                                value={fontFamily}
                                                onChange={(e) => setFontFamily(e.target.value)}
                                            >
                                                <option value="Montserrat, sans-serif">Montserrat (Deportiva / Moderna)</option>
                                                <option value="Impact, sans-serif">Impact (Clásica Pesada / Enérgica)</option>
                                                <option value="Roboto, sans-serif">Roboto (Limpia y Estándar)</option>
                                                <option value="Oswald, sans-serif">Oswald (Estilo Atletismo)</option>
                                                <option value="Bebas Neue, sans-serif">Bebas Neue (Titulares Altos)</option>
                                                <option value="Arial Black, sans-serif">Arial Black (Gruesa)</option>
                                            </select>
                                        </div>

                                        <div className="col-12 col-md-3">
                                            <label className="form-label fw-bold small text-secondary">
                                                Color Primario
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="color"
                                                    className="form-control form-control-color"
                                                    value={primaryColor}
                                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                                    title="Elegir color primario"
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control text-uppercase"
                                                    value={primaryColor}
                                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-3">
                                            <label className="form-label fw-bold small text-secondary">
                                                Color Secundario / Acento
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="color"
                                                    className="form-control form-control-color"
                                                    value={secondaryColor}
                                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                                    title="Elegir color secundario"
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control text-uppercase"
                                                    value={secondaryColor}
                                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* SWITCH ARCOS DE ESPUMA */}
                                        <div className="col-12">
                                            <div className="form-check form-switch p-3 bg-light rounded-3 border">
                                                <input
                                                    className="form-check-input ms-0 me-3"
                                                    type="checkbox"
                                                    id="enableFoamArchesModal"
                                                    checked={enableFoamArches}
                                                    onChange={(e) => setEnableFoamArches(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-bold text-dark" htmlFor="enableFoamArchesModal">
                                                    Activar Arcos de Espuma Patrocinados en Hitos
                                                </label>
                                                <p className="text-muted small mb-0 mt-1">
                                                    Al activarse, la pantalla gigante detonará una explosión de espuma del color correspondiente al superar cada kilómetro.
                                                </p>
                                            </div>
                                        </div>

                                        {/* LOGOS */}
                                        <div className="col-12 col-md-6">
                                            <FileDropzone
                                                label="Logo del Club / Marca Principal:"
                                                value={brandLogoUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="brandLogoUrl"
                                                uploadingKey={uploadingKey}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <FileDropzone
                                                label="Logo de Patrocinador Oficial:"
                                                value={sponsorLogoUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="sponsorLogoUrl"
                                                uploadingKey={uploadingKey}
                                            />
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* ========================================================================= */}
                            {/* PESTAÑA 2: PORTADA */}
                            {/* ========================================================================= */}
                            {activeTab === 'cover' && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                        <i className="bi bi-tv-fill me-2 text-primary"></i>
                                        Pantalla de Portada (Lienzo Gigante)
                                    </h6>

                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label fw-bold small text-secondary">
                                                Título Grande de Portada
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ej. ¡GRAN CARRERA CONTINUA 5K!"
                                                value={coverTitle}
                                                onChange={(e) => setCoverTitle(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-12">
                                            <FileDropzone
                                                label="Imagen de Fondo de Portada (1920x1080 Full HD):"
                                                value={coverBackgroundUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="coverBackgroundUrl"
                                                uploadingKey={uploadingKey}
                                                aspectHint="1920x1080 JPG/PNG/WebP"
                                                previewHeight="140px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ========================================================================= */}
                            {/* PESTAÑA 3: ESCENARIO & PARALLAX (2 CAPAS UNIFICADAS) */}
                            {/* ========================================================================= */}
                            {activeTab === 'scenery' && (
                                <div className="animate__animated animate__fadeIn">
                                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                        <i className="bi bi-layers-fill me-2 text-primary"></i>
                                        Capas de Fondo Parallax (Paisaje y Pista) y Celular de Aficionado
                                    </h6>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <FileDropzone
                                                label="Capa 1: Fondo / Paisaje Completo (Cielo + Casas / Estadio):"
                                                value={cityBackgroundUrl || skyBackgroundUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="cityBackgroundUrl"
                                                uploadingKey={uploadingKey}
                                                aspectHint="1920x1080 o 1920x600 Repetible"
                                                previewHeight="110px"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <FileDropzone
                                                label="Capa 2: Pista de Carreras (Suelo / Asfalto / Pasto):"
                                                value={trackBackgroundUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="trackBackgroundUrl"
                                                uploadingKey={uploadingKey}
                                                aspectHint="1920x400 Textura de Pista"
                                                previewHeight="110px"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <FileDropzone
                                                label="Fondo para Celular del Aficionado (Pantalla Móvil):"
                                                value={fanBackgroundUrl}
                                                onUpload={handleUploadAsset}
                                                onDelete={handleDeleteAsset}
                                                fieldName="fanBackgroundUrl"
                                                uploadingKey={uploadingKey}
                                                aspectHint="1080x1920 Vertical"
                                                previewHeight="110px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ========================================================================= */}
                            {/* PESTAÑA 4: ARCOS & ESPUMA */}
                            {/* ========================================================================= */}
                            {activeTab === 'checkpoints' && (
                                <div className="animate__animated animate__fadeIn">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom pb-2 mb-3">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0">
                                                <i className="bi bi-flag-fill me-2 text-warning"></i>
                                                Configuración de Arcos, Hitos y Espuma ({checkpoints.length} Hitos)
                                            </h6>
                                            <small className="text-muted">Define los puntos de control intermedios y el arco de meta final.</small>
                                        </div>
                                        <div className="d-flex gap-2 mt-2 mt-md-0">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={handleRegenerateCheckpoints}
                                                title="Regenerar hitos uniformemente"
                                            >
                                                <i className="bi bi-magic me-1"></i> Auto-Calcular ({totalKilometers}K)
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm fw-bold"
                                                onClick={handleAddCheckpoint}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> + Agregar Hito
                                            </button>
                                        </div>
                                    </div>

                                    {/* ARCO INFLABLE PREDETERMINADO PARA TODOS LOS HITOS */}
                                    <div className="card border-primary border-opacity-25 bg-primary bg-opacity-10 p-3 mb-4 rounded-3 shadow-sm">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <i className="bi bi-shield-shaded text-primary fs-5"></i>
                                            <span className="fw-bold text-dark">Imagen de Arco Inflable Predeterminada (para todos los kilómetros):</span>
                                        </div>
                                        <p className="text-muted small mb-2">
                                            Sube tu imagen de arco (PNG con fondo transparente). Se aplicará automáticamente a todos los arcos e hitos de la carrera.
                                        </p>
                                        <FileDropzone
                                            label="Arco Inflable Global (PNG):"
                                            value={defaultArchImageUrl}
                                            onUpload={handleUploadAsset}
                                            onDelete={handleDeleteAsset}
                                            fieldName="defaultArchImageUrl"
                                            uploadingKey={uploadingKey}
                                            aspectHint="PNG transparente en perspectiva"
                                            previewHeight="120px"
                                        />
                                    </div>

                                    {checkpoints.length === 0 ? (
                                        <div className="text-center py-4 bg-light rounded-3 border">
                                            <p className="text-muted mb-2">No hay hitos de kilómetros configurados.</p>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={handleRegenerateCheckpoints}
                                            >
                                                Generar Hitos Automáticamente
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {checkpoints.map((cp, idx) => (
                                                <div key={idx} className="p-3 bg-light rounded-3 border position-relative">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <span className="badge bg-dark fs-6">
                                                            Hito #{idx + 1} {idx === checkpoints.length - 1 ? '🏁 (Meta Final)' : ''}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveCheckpoint(idx)}
                                                            title="Eliminar este hito"
                                                        >
                                                            <i className="bi bi-trash-fill me-1"></i> Eliminar
                                                        </button>
                                                    </div>

                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-3">
                                                            <label className="form-label fw-bold small text-secondary">
                                                                Etiqueta / Nombre:
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={cp.labelText || cp.label || ''}
                                                                onChange={(e) => handleUpdateCheckpoint(idx, 'labelText', e.target.value)}
                                                                placeholder="Ej. Km 1, Meta..."
                                                            />
                                                        </div>

                                                        <div className="col-6 col-md-2">
                                                            <label className="form-label fw-bold small text-secondary">
                                                                Kilómetro:
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={cp.kilometerIndex || cp.kilometer || (idx + 1)}
                                                                onChange={(e) => handleUpdateCheckpoint(idx, 'kilometerIndex', Number(e.target.value))}
                                                                min="1"
                                                            />
                                                        </div>

                                                        <div className="col-6 col-md-2">
                                                            <label className="form-label fw-bold small text-secondary">
                                                                Progreso (%):
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={cp.triggerPercentage !== undefined ? cp.triggerPercentage : ''}
                                                                onChange={(e) => handleUpdateCheckpoint(idx, 'triggerPercentage', Number(e.target.value))}
                                                                min="1"
                                                                max="100"
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-2">
                                                            <label className="form-label fw-bold small text-secondary">
                                                                Color Espuma:
                                                            </label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="color"
                                                                    className="form-control form-control-color"
                                                                    value={cp.foamColorHex || '#00e5ff'}
                                                                    onChange={(e) => handleUpdateCheckpoint(idx, 'foamColorHex', e.target.value)}
                                                                    title="Color de espuma SkiaSharp"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-control text-uppercase"
                                                                    value={cp.foamColorHex || '#00e5ff'}
                                                                    onChange={(e) => handleUpdateCheckpoint(idx, 'foamColorHex', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="col-12 col-md-3">
                                                            <FileDropzone
                                                                label="Arco Gráfico (PNG):"
                                                                value={cp.archImageUrl || cp.bannerImageUrl || ''}
                                                                onUpload={handleUploadAsset}
                                                                onDelete={handleDeleteAsset}
                                                                fieldName="checkpoints"
                                                                subIndex={idx}
                                                                subField="archImageUrl"
                                                                uploadingKey={uploadingKey}
                                                                aspectHint="Estructura Arco PNG"
                                                                previewHeight="60px"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ========================================================================= */}
                            {/* PESTAÑA 5: CORREDORES / ZONAS */}
                            {/* ========================================================================= */}
                            {activeTab === 'runners' && (
                                <div className="animate__animated animate__fadeIn">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom pb-2 mb-3">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0">
                                                <i className="bi bi-people-fill me-2 text-primary"></i>
                                                Avatares de Corredores por Zona ({runners.length} Zonas)
                                            </h6>
                                            <small className="text-muted">Personaliza los sprites de corredores, victoria y colores de carril para cada grada.</small>
                                        </div>
                                        <div className="d-flex gap-2 mt-2 mt-md-0">
                                            <button
                                                type="button"
                                                className="btn btn-outline-info btn-sm"
                                                onClick={handleSyncStadiumZones}
                                                title="Sincronizar nombres desde la sala activa"
                                            >
                                                <i className="bi bi-arrow-repeat me-1"></i> Sincronizar Zonas Sala
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm fw-bold"
                                                onClick={handleAddRunner}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> + Agregar Zona
                                            </button>
                                        </div>
                                    </div>

                                    {runners.length === 0 ? (
                                        <div className="text-center py-4 bg-light rounded-3 border">
                                            <p className="text-muted mb-2">No hay zonas o corredores registrados.</p>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={handleAddRunner}
                                            >
                                                Crear Zona 1
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {runners.map((r, idx) => (
                                                <div key={idx} className="p-3 bg-light rounded-3 border">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span
                                                                className="badge px-3 py-2 fw-bold text-uppercase"
                                                                style={{ backgroundColor: r.themeColorHex || r.color || '#0d6efd', color: '#fff' }}
                                                            >
                                                                Zona #{idx + 1}: {r.displayName || r.zoneName || `Zona ${idx + 1}`}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveRunner(idx)}
                                                            disabled={runners.length <= 1}
                                                            title="Eliminar este corredor"
                                                        >
                                                            <i className="bi bi-trash-fill"></i>
                                                        </button>
                                                    </div>

                                                    <div className="row g-3 mt-1">
                                                        <div className="col-12 col-md-4">
                                                            <label className="form-label fw-bold small text-secondary">
                                                                Nombre de la Zona:
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm mb-2"
                                                                value={r.displayName || r.zoneName || ''}
                                                                onChange={(e) => handleUpdateRunner(idx, 'displayName', e.target.value)}
                                                                placeholder="Ej. Grada Norte, Águilas..."
                                                            />

                                                            <label className="form-label fw-bold small text-secondary">
                                                                Identificador Clave (ID):
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm mb-2"
                                                                value={r.zoneIdentifier || r.zoneId || ''}
                                                                onChange={(e) => handleUpdateRunner(idx, 'zoneIdentifier', e.target.value)}
                                                                placeholder="Ej. 1, 2, norte..."
                                                            />

                                                            <label className="form-label fw-bold small text-secondary">
                                                                Color de Carril / Estela:
                                                            </label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="color"
                                                                    className="form-control form-control-color"
                                                                    value={r.themeColorHex || r.color || '#0d6efd'}
                                                                    onChange={(e) => handleUpdateRunner(idx, 'themeColorHex', e.target.value)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-control text-uppercase"
                                                                    value={r.themeColorHex || r.color || '#0d6efd'}
                                                                    onChange={(e) => handleUpdateRunner(idx, 'themeColorHex', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="col-12 col-md-4">
                                                            <FileDropzone
                                                                label="Avatar Corriendo (Sprite/PNG):"
                                                                value={r.avatarRunUrl || r.avatarUrl || ''}
                                                                onUpload={handleUploadAsset}
                                                                onDelete={handleDeleteAsset}
                                                                fieldName="runners"
                                                                subIndex={idx}
                                                                subField="avatarRunUrl"
                                                                uploadingKey={uploadingKey}
                                                                aspectHint="500x500 PNG Transparente"
                                                                previewHeight="90px"
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-4">
                                                            <FileDropzone
                                                                label="Avatar Ganador / Victoria (PNG):"
                                                                value={r.avatarWinUrl || r.spriteUrl || ''}
                                                                onUpload={handleUploadAsset}
                                                                onDelete={handleDeleteAsset}
                                                                fieldName="runners"
                                                                subIndex={idx}
                                                                subField="avatarWinUrl"
                                                                uploadingKey={uploadingKey}
                                                                aspectHint="500x500 PNG Celebración"
                                                                previewHeight="90px"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                        </form>
                    </div>

                    {/* PIE DEL MODAL CON BOTONES DE ACCIÓN */}
                    <div className="card-footer bg-light border-top d-flex justify-content-between align-items-center py-3 px-4 flex-shrink-0">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={isSaving || Boolean(uploadingKey)}
                        >
                            Cancelar
                        </button>

                        <div className="d-flex gap-2">
                            {activeTab !== 'general' && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        const tabs = ['general', 'cover', 'scenery', 'checkpoints', 'runners'];
                                        const curIdx = tabs.indexOf(activeTab);
                                        if (curIdx > 0) setActiveTab(tabs[curIdx - 1]);
                                    }}
                                >
                                    <i className="bi bi-arrow-left me-1"></i> Anterior
                                </button>
                            )}

                            {activeTab !== 'runners' ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => {
                                        const tabs = ['general', 'cover', 'scenery', 'checkpoints', 'runners'];
                                        const curIdx = tabs.indexOf(activeTab);
                                        if (curIdx < tabs.length - 1) setActiveTab(tabs[curIdx + 1]);
                                    }}
                                >
                                    Siguiente <i className="bi bi-arrow-right ms-1"></i>
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="btn btn-success fw-bold px-4 shadow-sm"
                                onClick={handleSaveTheme}
                                disabled={isSaving || Boolean(uploadingKey)}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando Tema...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        {themeToEdit?.id ? 'Guardar Cambios' : 'Crear Tema'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export { CarreraContinuaThemeModal };
export default CarreraContinuaThemeModal;
